import type { UIMatch } from 'react-router-dom';

import { createPath, resolvePath, useMatches } from 'react-router-dom';
import uniqBy from 'lodash/uniqBy';
import React from 'react';

export interface BreadcrumbTitle {
  template: `:${string}`;
  value: string;
}
export default function useRouterBreadcrumbs() {
  const [breadCrumbTitles, setBreadCrumbTitles] = React.useState<
    Array<BreadcrumbTitle>
  >([]);
  const matches = useMatches() as UIMatch<
    unknown,
    {
      title?: string;
      index?: true;
      breadcrumbTo?: string | false;
      navAncestorMatch?: false;
      hideBreadcrumbs?: true;
    }
  >[];
  const filteredMatches = React.useMemo(() => {
    return matches
      .filter(
        (match) =>
          match.pathname !== '/' &&
          match.pathname !== '' &&
          match?.handle?.index !== true,
      )
      .map((match) => {
        let title = match.handle?.title;
        const breadCrumb = breadCrumbTitles.find(
          (title) => title.template === match?.handle?.title,
        );
        if (breadCrumb) {
          title = breadCrumb.value;
        }
        return {
          pathname: match.pathname,
          // Layout-only parents can suppress links or point at an existing list.
          to:
            match.handle?.breadcrumbTo === false
              ? (false as const)
              : match.handle?.breadcrumbTo
                ? createPath(
                    resolvePath(match.handle.breadcrumbTo, match.pathname),
                  )
                : match.pathname,
          // A layout whose pathname coincides with its own index route (no
          // path segment of its own) must not count as an active ancestor
          // for nav-highlight purposes — see NavigationItem's `selected`.
          isNavAncestor: match.handle?.navAncestorMatch !== false,
          hideBreadcrumbs: match.handle?.hideBreadcrumbs,
          title,
        };
      });
  }, [breadCrumbTitles, matches]);

  const setBreadCrumbTitle = React.useCallback(
    (title: Array<BreadcrumbTitle> | BreadcrumbTitle | undefined) => {
      if (!title) return;
      if (title instanceof Array) {
        setBreadCrumbTitles((prev) => uniqBy([...title, ...prev], 'template'));
        return;
      }
      setBreadCrumbTitles((prev) => uniqBy([title, ...prev], 'template'));
    },
    [setBreadCrumbTitles],
  );

  return {
    breadCrumbTitles,
    matches: filteredMatches,
    setBreadCrumbTitle,
  };
}
